<template>
  <v-card class="rounded-lg elevation-3" height="100vh">
    <v-card-title class="headline primary white--text py-4 d-flex justify-space-between">
      <span class="d-flex align-center">
        <v-icon left large color="white">mdi-view-grid</v-icon>
        Catálogo de Artículos
      </span>
      <v-btn icon @click="$emit('close-item')" class="white--text">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4">
      <v-row>
        <v-col cols="12" sm="4" md="3">
          <v-card outlined class="mb-4 filter-card">
            <v-card-title class="subtitle-1">
              <v-icon left color="primary">mdi-filter-variant</v-icon>
              Filtros
            </v-card-title>
            <v-card-text>
              <v-text-field v-model="search" label="Buscar por descripcion o código" prepend-inner-icon="mdi-magnify"
                clearable :loading="loading" outlined dense :disabled="loading" hide-details
                class="mb-4"></v-text-field>
              <v-text-field v-model="requestFilter" label="ID de solicitud" prepend-inner-icon="mdi-file-document-outline"
                clearable outlined dense hide-details class="mb-4"></v-text-field>
              <v-autocomplete v-model="selectedCategory" :items="categoriesWithAll" item-text="name" item-value="id"
                label="Categoría" outlined dense hide-details class="mb-4"></v-autocomplete>
              <v-autocomplete v-model="selectedWarehouse" :items="itemsWarehouse" color="cyanText" label="Almacen"
                item-value="id" item-text="name" prepend-inner-icon="business" outlined required dense
                @change="get_items_request">
              </v-autocomplete>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="8" md="9">
          <v-data-table :headers="headers" :items="filteredItems" :search="search" :loading="loading" :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, 100],
              'items-per-page-text': 'Artículos por página',
            }" height="calc(100vh - 170px)" fixed-header class="elevation-1" item-key="item_key">
            <template v-slot:item.image="{ item }">
              <v-avatar size="40" class="ma-2">
                <v-img :src="item.item_image" :alt="item.item_name"></v-img>
              </v-avatar>
            </template>

            <template v-slot:item.request_id="{ item }">
              <span class="font-weight-medium">{{ (item.request_id || "").trim() }}</span>
            </template>

            <template v-slot:item.quantity="{ item }">
              <v-chip :color="getQuantityColor(item)" text-color="white" small label>
                {{ item.warehouse_discount ? item.warehouse_discount.quantity : 0 }}
              </v-chip>
            </template>

            <template v-slot:item.price="{ item }">
              <span class="font-weight-bold">{{ currencyFormat(getPrice(item)) }}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" small @click="selectItem(item)" :disabled="!canSelectItem(item)" v-bind="attrs"
                    v-on="on">
                    <v-icon left small>mdi-plus</v-icon>
                    Agregar
                  </v-btn>
                </template>
                <span>{{ getTooltipText(item) }}</span>
              </v-tooltip>
            </template>

            <template v-slot:no-data>
              <v-alert type="info" class="ma-2">No se encontraron artículos</v-alert>
            </template>

            <template v-slot:progress>
              <v-progress-linear indeterminate color="primary"></v-progress-linear>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import moment from "moment";

export default {
  name: "ArticlesCatalogue",

  data: () => ({
    items: [],
    loading: true,
    search: "",
    requestFilter: "",
    itemsWarehouse: [],
    headers: [
      { text: "ID", align: "start", value: "item_id", width: "5%" },
      { text: "Solicitud", value: "request_id", width: "8%" },
      { text: "Imagen", value: "image", width: "8%" },
      { text: "Descripción", value: "item_name" },
      { text: "Unidad", value: "unit_name", width: "8%", align: "center" },
      { text: "Almacén", value: "warehouse", width: "15%", align: "center" },
      { text: "Cantidad", value: "quantity", width: "8%", align: "center" },
      { text: "Precio", value: "price", width: "10%", align: "end" },
      { text: "Acciones", value: "actions", sortable: false, width: "8%" },
    ],
    selectedCategory: null,
    selectedWarehouse: null,
    categories: [],
    categoryTree: [],
    snackbar: false,
    snackbarText: "",
    snackbarColor: "success",
  }),

  props: {
    data: {
      type: Object,
      required: true,
    },
    showWarehouse: {
      type: Boolean,
      default: true,
    },
    removeItemSelected: {
      type: Boolean,
      default: true,
    },
    selected_currencies: {
      type: Number,
    },
    itbis: {
      type: Array,
      required: true,
    },
    type: {
      type: Number,
      default: 1,
      validator: (value) => [1, 2].includes(value),
    },
  },

  computed: {
    filteredItems() {
      const filter = (this.requestFilter || "").trim();
      if (!filter) return this.items;
      const normalized = filter.replace(/^0+/, "");
      return this.items.filter((item) => {
        const requestId = String(parseInt(item.request_id, 10) || "");
        return normalized ? requestId.includes(normalized) : true;
      });
    },
    categoriesWithAll() {
      return [{ id: "all", name: "Todas las categorías" }, ...this.categories];
    },
    warehousesWithAll() {
      return [{ id: "all", name: "Todos los almacenes" }, ...this.itemsWarehouse];
    },
  },

  created() {
    this.init();
  },

  methods: {
    getPrice(item) {
      if (!this.selected_currencies || !item.item_currencies?.length) {
        return 0;
      }

      const currency = item.item_currencies.find(
        (element) => element.currency_id === this.selected_currencies
      );
      if (!currency) return 0;

      const itbis = parseFloat(
        this.itbis.find((element) => element.id === item.tax_type_id)?.value || 0
      );

      const basePrice = this.type === 1 ? currency.cost_price : currency.sale_price;
      const totalPrice = basePrice * (1 + itbis);

      if (this.type === 1) {
        item.cost_price = totalPrice;
      } else {
        item.sale_price = totalPrice;
      }

      return totalPrice;
    },

    selectItem(item) {
      this.$emit("selected-item", item);

      if (this.removeItemSelected) {
        const index = this.items.findIndex((i) => i.item_id === item.item_id);
        if (index !== -1) {
          this.items.splice(index, 1);
        }
      }

      this.showSnackbar("Artículo agregado correctamente", "success");
    },

    getWarehouse(items) {
      return items.item_in_warehouses
        .map((ware) => {
          const matchingWarehouse = this.itemsWarehouse.find(
            (itWare) => ware.warehouse_id === itWare.id
          );
          if (matchingWarehouse) {
            return {
              id: ware.warehouse_id,
              name: matchingWarehouse.name,
              quantity: ware.quantity,
            };
          }
          return null;
        })
        .filter(Boolean);
    },

    async getCategories() {
      try {
        let filters = `where ITEM_CATEGORIES.BUSINESS_UNIT_ID=${this.data.business}`;

        if (this.data.location != null) {
          filters += ` AND LOCATION_ID=${this.data.location}`;
        }

        const resultCategories = await this.api_Post("inventory/GetCategories", {
          filters,
        });

        if (!resultCategories.ok) {
          throw new Error(`HTTP error! status: ${resultCategories.status}`);
        }

        const data = await resultCategories.json();

        const formattedCategories = data.map((item) => ({
          id: item.id,
          parent: item.parent_category_id,
          name: item.name,
          note: item.note,
          business_unit_id: item.business_unit_id,
          bussines_unit_description: item.bussines_unit_description,
          location_id: item.location_id,
          location_description: item.location_description,
          code: item.code,
          account_type_id: item.account_type_id,
          dgii_category_id: item.dgii_category_id,
          active: item.active,
        }));

        this.categoryTree = this.nestCategories(formattedCategories);
        this.categories = this.flattenCategoryTree(this.categoryTree);
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.showSnackbar("Error al cargar las categorías", "error");
      }
    },

    nestCategories(items, id = null, link = "parent") {
      return items
        .filter((item) => item[link] === id)
        .map((item) => ({ ...item, children: this.nestCategories(items, item.id) }));
    },

    flattenCategoryTree(tree, prefix = "") {
      return tree.reduce((acc, category) => {
        const flatCategory = {
          id: category.id,
          name: prefix + category.name,
        };
        acc.push(flatCategory);

        if (category.children && category.children.length > 0) {
          acc.push(
            ...this.flattenCategoryTree(category.children, `${prefix}${category.name} > `)
          );
        }

        return acc;
      }, []);
    },

    async init() {
      try {
        this.loading = true;
        this.selectedWarehouse = this.data.warehouse;
        await this.get_items_request();
      } catch (error) {
        console.error("Error initializing data:", error);
        this.showSnackbar(
          error.message || "An error occurred while loading data",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async get_items_request() {
      let filtros = ` WHERE difference > 0 AND REQUEST_WITH_DETAILS.BUSINESS_UNIT_ID=${this.data.business} AND REQUEST_WITH_DETAILS.LOCATION_ID=${this.data.location} AND WAREHOUSE_ID=${this.selectedWarehouse}`;

      try {
        const request = await this.api_Post("shopping/articles_for_quotes", {
          filters: filtros,
        });
        const data = await request.json();

        if (request.status == 200) {
          this.items = await data.map(item => ({
            ...item,
            item_key: `${item.item_id}-${item.request_id}`
          }))



          for (let key in this.items) {
            this.items[key].warehouse_discount = { quantity: parseFloat(this.items[key].difference) };
            this.items[key].requested_quantity = parseFloat(this.items[key].quantity);
          }
          if (this.itemsWarehouse.length == 0) {
            const warehousesResponse = await this.api_Get(`inventory/warehouseForLocation/${this.data.location}`);
            this.itemsWarehouse = await warehousesResponse.json();
          }
        } else {
          this.$toast.error("Error: " + data.error);
        }
      } catch (error) {
        this.$toast.error("Error: " + error);
      }
    },
    getQuantityColor(item) {
      const quantity = item.warehouse_discount ? item.warehouse_discount.quantity : 0;
      if (quantity > 10) return "green";
      if (quantity > 5) return "orange";
      return "red";
    },

    updateItemWarehouse(item) {
      // Implement the logic to update the item's warehouse
      this.showSnackbar("Almacén actualizado correctamente", "success");
    },

    canSelectItem(item) {
      if (this.type == 1) {
        return true;
      }

      return (
        item.negative_sale ||
        item.item_type_id === 2 ||
        (item.warehouse_discount != null && item.warehouse_discount.quantity > 0)
      );
    },

    getTooltipText(item) {
      if (item.negative_sale) {
        return "Este artículo permite ventas negativas";
      } else if (item.item_type_id === 2) {
        return "Este es un servicio y puede ser vendido";
      } else if (
        item.warehouse_discount != null &&
        item.warehouse_discount.quantity > 0
      ) {
        return "Este artículo está disponible en el inventario";
      } else {
        return "Este artículo no está disponible para la venta";
      }
    },

    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-y: auto;
}

.filter-card {
  background-color: #f5f5f5;
  border-radius: 8px;
}

.v-card__title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-chip {
  font-weight: 500;
}

.v-btn {
  text-transform: none;
}

.v-data-table ::v-deep .v-data-table__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
</style>
